import * as React from 'react';
import type { Localized, StoryCard, VocabPair } from '@org/model';
import type { StoryLabels } from './labels';
import type { StoryLang, StoryWoman } from './story-types';

/** Text in the viewer language, falling back to the other language when it is empty. */
export function pickLocalized(
  value: Localized | null | undefined,
  lang: StoryLang,
): { text: string; lang: StoryLang } {
  const other: StoryLang = lang === 'en' ? 'pt' : 'en';
  const own = value?.[lang]?.trim() ? value[lang] : '';
  if (own) return { text: own, lang };
  const alt = value?.[other]?.trim() ? value[other] : '';
  return alt ? { text: alt, lang: other } : { text: '', lang };
}

/** BCP-47 tag for the `lang` attribute, so screen readers pronounce the text right. */
export function storyLangAttr(lang: StoryLang): string {
  return lang === 'pt' ? 'pt-BR' : 'en';
}

/** `*word*` → highlighted word. */
export function renderStoryMarkup(text: string): React.ReactNode {
  const parts = text.split(/\*(.+?)\*/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) => (i % 2 === 1 ? <mark key={i}>{part}</mark> : <React.Fragment key={i}>{part}</React.Fragment>));
}

const stripMarkup = (text: string) => text.replace(/\*(.+?)\*/g, '$1');

export function storyFirstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? '';
}

/** Her quote in the viewer language (Portuguese falls back to the English quote, and vice-versa). */
export function storyWomanQuote(woman: StoryWoman, lang: StoryLang): { text: string; lang: StoryLang } {
  return pickLocalized({ en: woman.quote ?? '', pt: woman.quotePt ?? '' }, lang);
}

export function storyWomanCharacteristic(woman: StoryWoman, lang: StoryLang): string {
  return pickLocalized({ en: woman.characteristicEn ?? '', pt: woman.characteristic ?? '' }, lang).text;
}

/** The optional small heading of a card: `title[lang]` (with fallback) or nothing. */
export function storyCardTitle(card: StoryCard, lang: StoryLang): { text: string; lang: StoryLang } {
  return pickLocalized(card.title, lang);
}

export const STORY_CARD_LABEL_KEYS = {
  text: 'cardText',
  video: 'cardVideo',
  notebook: 'cardNotebook',
  quote: 'cardQuote',
} as const satisfies Record<StoryCard['type'], keyof StoryLabels>;

export type StoryPage =
  | { kind: 'reveal' }
  | { kind: 'card'; card: StoryCard }
  | { kind: 'closing' };

export function buildStoryPages(cards: readonly StoryCard[]): StoryPage[] {
  return [{ kind: 'reveal' }, ...cards.map((card) => ({ kind: 'card' as const, card })), { kind: 'closing' }];
}

/** The text a student reads on a page — only used to time the auto-advance. */
function readingTextOf(page: StoryPage, woman: StoryWoman, lang: StoryLang): string {
  if (page.kind === 'reveal') return `${pickLocalized(woman.tagline, lang).text} ${woman.name}`;
  if (page.kind === 'closing') return '';
  const card = page.card;
  switch (card.type) {
    case 'text':
      return stripMarkup(pickLocalized(card.body, lang).text);
    case 'notebook':
      return lang !== card.originalLang
        ? `${stripMarkup(pickLocalized(card.gloss, lang).text)} ${card.original}`
        : card.original;
    case 'quote':
      return `${pickLocalized(card.body, lang).text} ${storyWomanQuote(woman, lang).text}`;
    default:
      return '';
  }
}

/**
 * How long a page stays before auto-advancing (prototype formula): 5 s + 50 ms
 * per character, between 6 and 18 s; the reveal gets 3 s more for its
 * animation. Video and closing pages never time out.
 */
export function storyPageDuration(page: StoryPage, woman: StoryWoman, lang: StoryLang): number {
  if (page.kind === 'closing') return Infinity;
  if (page.kind === 'card' && page.card.type === 'video') return Infinity;
  const ms = Math.min(18000, Math.max(6000, 5000 + readingTextOf(page, woman, lang).length * 50));
  return page.kind === 'reveal' ? ms + 3000 : ms;
}

export function StoryVocabRow({ vocab, lang }: { vocab?: VocabPair[]; lang: StoryLang }) {
  const pairs = (vocab ?? []).filter((v) => v.en.trim() && v.pt.trim());
  if (lang !== 'en' || pairs.length === 0) return null;
  return (
    <div className="zs-vocab" lang="en" data-testid="story-vocab">
      {pairs.map((v, i) => (
        <span key={i}>
          <b>{v.en}</b>
          <i lang="pt-BR">{v.pt}</i>
        </span>
      ))}
    </div>
  );
}

/** The small heading of a card — rendered only when the card has a title. */
export function StoryCardHeading({ card, lang }: { card: StoryCard; lang: StoryLang }) {
  const title = storyCardTitle(card, lang);
  if (!title.text) return null;
  return (
    <h2 className="zs-kick" lang={storyLangAttr(title.lang)} data-testid="story-card-title">
      {title.text}
    </h2>
  );
}
